import { observable, action, reaction, runInAction, computed, makeObservable } from 'mobx';
import { FileOperationsStore, WorkflowInstanceStore, ProjectsStore, TabsStore } from '../../common/stores';
import { PreviewsVisualStore } from '.';
import { ImagePreviewSize } from '../../documents/types/types';
import { SessionsVisualStore } from '../../sessions/stores';
import { types } from '../../common/services';
import { PackageListItemType, SessionStates } from '../../common/services/types';
import { message } from 'antd';

export default class AnalyzeVisualStore {
    sessionsInitializing: boolean = true;

    showProgressModal: boolean = false;

    processedPackageName: string = '';

    processedPackageId: string | undefined = '';

    currentSessionId: string | undefined = undefined;

    runningSessionIds: string[] = [];

    sessionToHighlight: string | null = null;

    get recentFinishedSessions() {
        return this.sessionsStore.recentFinishedSessions;
    }

    get openSessions() {
        return this.sessionsStore.openSessions;
    }

    get currentSessionInProgress() {
        return this.openSessions.find(s => s.id === this.workflowInstanceStore.sessionId);
    }

    get projectsLoading() {
        return this.projectsStore.isLoading;
    }

    constructor(
        private sessionsStore: SessionsVisualStore,
        private workflowInstanceStore: WorkflowInstanceStore,
        private projectsStore: ProjectsStore,
        private previewsStore: PreviewsVisualStore,
        private tabsStore: TabsStore,
        private fileStore: FileOperationsStore
    ) {
        makeObservable(this, {
            sessionsInitializing: observable,
            showProgressModal: observable,
            processedPackageName: observable,
            processedPackageId: observable,
            currentSessionId: observable,
            runningSessionIds: observable,
            sessionToHighlight: observable,
            recentFinishedSessions: computed,
            openSessions: computed,
            currentSessionInProgress: computed,
            projectsLoading: computed,
            setCurrentSessionId: action.bound,
            addRunningSessionId: action.bound,
            removeRunningSessionId: action.bound,
            setShowProgressModal: action.bound,
            setProcessedPackageName: action.bound,
            setProcessedPackageId: action.bound,
            getPreviewForCurrentRunningSession: action.bound,
            analyzePackage: action.bound,
            analyzeMultiplePackages: action.bound,
            openProgressModalForSession: action.bound,
            moveSessionToArchive: action.bound,
            startSessionFromExisting: action.bound,
            switchToSession: action.bound,
            loadSessions: action.bound,
            setSessionToHighlight: action.bound
        });

        reaction(
            () => this.openSessions,
            () => {
                this.runningSessionIds.forEach(id => {
                    this.handleSessionStateChange(id);
                });
            }
        );

        reaction(
            () => this.fileStore.isSessionProgressDialogVisible,
            p => {
                if (p) {
                    tabsStore.resetSelection();
                    this.setShowProgressModal(true);
                }
            }
        );

        reaction(
            () => this.tabsStore.sessionTabReloading,
            sessionData => {
                if (sessionData) {
                    this.setCurrentSessionId(sessionData.sessionId);
                    this.setShowProgressModal(true);
                    this.setProcessedPackageName(sessionData.packageName);
                    this.setProcessedPackageId(sessionData.packageId);
                    this.addRunningSessionId(sessionData.sessionId);
                    void this.handleSessionStateChange(sessionData.sessionId);
                }
            }
        );

        this.projectsStore.sessionChanges.subscribe(session => {
            if (!session) {
                return;
            }

            if (session.state === 'Aborted') {
                this.handleAbortedSession(session);
                this.sessionsStore.loadRecentFinishedSessions().then(() => this.setSessionToHighlight(session.id));
            }
        });
    }

    setSessionToHighlight(sessionToHighlight: string | null) {
        this.sessionToHighlight = sessionToHighlight;
    }

    setCurrentSessionId(id: string | undefined) {
        this.currentSessionId = id;
    }

    addRunningSessionId(id: string) {
        this.runningSessionIds.push(id);
    }

    removeRunningSessionId(id: string) {
        this.runningSessionIds = this.runningSessionIds.filter(i => i !== id);
    }

    setShowProgressModal(show: boolean) {
        if (!show) {
            this.fileStore.setIsSessionProgressDialogVisible(false);
        }
        this.showProgressModal = show;
    }

    setProcessedPackageName(name: string) {
        this.processedPackageName = name;
    }

    setProcessedPackageId(id: string | undefined) {
        this.processedPackageId = id;
    }

    async getPreviewForCurrentRunningSession() {
        if (this.processedPackageId && !this.previewsStore.largePreviewUrls[this.processedPackageId]) {
            await this.previewsStore.getPreviews(this.processedPackageId, ImagePreviewSize.Large);
        }
    }

    async analyzePackage(
        pkgId: string,
        appId: string,
        pkgName: string,
        extension: string,
        type: PackageListItemType = PackageListItemType.Package,
        appName: string = ''
    ) {
        this.setShowProgressModal(true);
        this.projectsStore.setCurrentProject(this.projectsStore.selectedPackageProject);
        this.setProcessedPackageName(pkgName);
        this.setProcessedPackageId(pkgId);
        const callback = (sessionId: string) => {
            this.setCurrentSessionId(sessionId);
            this.addRunningSessionId(sessionId);
        };
        if (type === PackageListItemType.Package) {
            const accepted = await this.workflowInstanceStore.startSession(pkgId, appId, pkgName, extension, callback);
            if (!accepted) {
                this.setShowProgressModal(false);
                return;
            }
            if (this.currentSessionId) {
                await this.handleSessionStateChange(this.currentSessionId);
            }
        } else if (type === PackageListItemType.PackageSet) {
            await this.workflowInstanceStore.startSessionForPackageSet(pkgId, appId, extension, pkgName, callback);
        }

        this.workflowInstanceStore.setApplicationName(appName);
    }

    async analyzeMultiplePackages(
        pkgIds: string[],
        appId: string,
        pkgName: string,
        extension: string,
        appName: string = ''
    ) {
        this.setShowProgressModal(true);
        this.projectsStore.setCurrentProject(this.projectsStore.selectedPackageProject);
        const allItems = [...this.projectsStore.selectedPackageModels, ...this.projectsStore.packageListItems];
        const selectedPackageNames = pkgIds
            .map(id => allItems.find(p => p.id === id))
            .filter(Boolean)
            .map(p => p!.fileName);
        const pkgNamesString = selectedPackageNames.join(', ');
        this.setProcessedPackageName(pkgNamesString);
        this.setProcessedPackageId(pkgIds[0]);
        await this.workflowInstanceStore.startSessionForMultiplePackages(
            pkgIds,
            appId,
            extension,
            (sessionId: string) => {
                this.setCurrentSessionId(sessionId);
                this.addRunningSessionId(sessionId);
            }
        );
        this.workflowInstanceStore.setApplicationName(appName);
    }

    openProgressModalForSession(sessionModel: unknown) {
        let session = sessionModel as types.ApplicationSession;
        this.setProcessedPackageName(session.packageName);
        this.setProcessedPackageId(session.packageId ?? session.packageSetId);
        this.setCurrentSessionId(session.id);
        this.projectsStore.setCurrentProject(session.projectId);
        this.addRunningSessionId(session.id);
        runInAction(() => {
            this.workflowInstanceStore.sessionId = session.id;
        });
        this.setShowProgressModal(true);
    }

    async moveSessionToArchive(sessionId: string) {
        await this.workflowInstanceStore.updateSession(sessionId, 'Archived' as SessionStates);
        this.sessionsStore.moveSessionToArchive(sessionId);
    }

    startSessionFromExisting(session: types.ApplicationSession) {
        if (session.packageId) {
            this.workflowInstanceStore.startSession(
                session.packageId,
                session.applicationDefinitionId,
                session.packageName,
                session.applicationExtension
            );
        } else if (session.packageSetId) {
            this.workflowInstanceStore.startSessionForPackageSet(
                session.packageSetId,
                session.applicationDefinitionId,
                session.applicationExtension,
                session.packageName
            );
        }
    }

    switchToSession(session: types.ApplicationSession) {
        this.sessionsStore.switchToSession(session);
    }

    async loadSessions() {
        try {
            await Promise.all([this.sessionsStore.loadOpenSessions(), this.sessionsStore.loadRecentFinishedSessions()]);
        } finally {
            if (this.sessionsInitializing) {
                runInAction(() => {
                    this.sessionsInitializing = false;
                });
            }
        }
    }

    private handleSessionStateChange = async (sessionId: string) => {
        let currentSession = this.openSessions.find(s => s.id === sessionId);
        if (!currentSession) {
            currentSession = (await this.sessionsStore.getSessionById(sessionId)) ?? undefined;
        }

        const sessionProcessed = currentSession && ['Finished', 'WaitingAction'].some(s => s === currentSession?.state);
        if (currentSession?.state === 'Aborted') {
            this.handleAbortedSession(currentSession);
            return;
        }

        if (this.runningSessionIds && this.runningSessionIds.length && sessionProcessed) {
            let session = this.sessionsStore.openSessions.find(s => s.id === sessionId);
            // if (this.tabsStore.sessionTabReloading) {
            //     session = this.sessionsStore.openSessions.find(s => s.id === this.tabsStore.sessionTabReloading?.sessionId);
            // }
            if (session) {
                const makeNewTabFocused =
                    this.tabsStore.sessionTabReloading?.sessionId === sessionId ||
                    (this.showProgressModal && sessionId === this.currentSessionId);
                this.tabsStore.setSessionTabReloading(undefined);
                this.workflowInstanceStore.setApplicationName(session.applicationName);
                this.tabsStore.addTab({
                    id: session.id,
                    title: session.packageName,
                    isSelected: makeNewTabFocused,
                    type: session.isBulkSession ? 'multidocument' : 'document',
                    metadata: {
                        packageId: session.packageId,
                        packageName: session.packageName,
                        sessionId: session.id,
                        uiIsLoading: true,
                        dynamicUI: undefined,
                        sessionError: session.error,
                        sessionState: session.state,
                        runtimeSessionId: session.runtimeSessionId,
                        packageSetId: session.packageSetId,
                        projectName: session.projectName,
                        projectId: session.projectId,
                        isReloadable: session.isReloadable,
                        customUI: session.capabilities?.customUI,
                        isBulk: session.isBulkSession,
                        applicationData: {
                            appName: session.applicationName,
                            appId: session.applicationDefinitionId,
                            appExtension: session.applicationExtension,
                            appSettings: session.applicationSettings,
                            iotaApplication: session.iotaApplication
                        }
                    }
                });
                if (makeNewTabFocused) {
                    this.setShowProgressModal(false);
                    this.setCurrentSessionId(undefined);
                }
                this.removeRunningSessionId(session.id);
            }
        }
    };

    private handleAbortedSession(session: { id: string; error?: string }) {
        if (!this.runningSessionIds.includes(session.id)) {
            return;
        }
        if (session.error) {
            message.error(session.error);
        }
        if (this.currentSessionId === session.id) {
            this.setShowProgressModal(false);
            this.setCurrentSessionId(undefined);
            runInAction(() => {
                this.workflowInstanceStore.isPdfHandleInProgress = false;
            });
            this.workflowInstanceStore.setReloadInProgress(false);
            this.tabsStore.setSessionTabReloading(undefined);
        }
        this.removeRunningSessionId(session.id);
    }
}
